/**
 * D460: badges you can follow. A badge counts only things waiting that a visit settles, never
 * things the player could buy. Each tab's count is the number of waiting things it holds, and
 * every level below it (the segment, the card, the branch, the item) is read from the same list,
 * so the counts always agree.
 *
 * D461: goals, rosettes and the week's prize pay the moment they are met, so there is no claim
 * step; a badge counts them while they are new, since the player last looked on this phone.
 * Pure: the caller passes the time and what was seen.
 */
import {
  idleFestivalAvailable,
  idleEventNow,
  idleFestivalsThisGeneration,
  idleLongGoalsMet,
  idleOrders,
  idlePerkCost,
  idlePerkTree,
  idleRosettes,
  type IdleWorldState,
} from '@hedgerow/sim';
import type { IdleContent } from '@hedgerow/data';
import type { IdleTab } from './store.ts';

/** One thing waiting, and where it sits, top to bottom. */
export interface Waiting {
  /** The tab's segment that holds it (`year`, `good`), or null for a tab without segments. */
  segment: string | null;
  /** The card that holds it: the card's test id. */
  card: string;
  /** Branch chips that hold a marked item, for their dots. */
  branches: string[];
  /** The marked items inside the card: step ids, or `order-<index>`. */
  items: string[];
}

export interface TabBadge {
  /** The tab's count: one for each thing waiting. */
  count: number;
  waiting: Waiting[];
  /** The count carried down to each segment that holds some of it. */
  segments: Record<string, number>;
  /** The count carried down to each card that holds some of it. */
  cards: Record<string, number>;
  branches: string[];
  items: string[];
}

export type IdleBadges = Record<IdleTab, TabBadge>;

function tabBadge(waiting: Waiting[]): TabBadge {
  const segments: Record<string, number> = {};
  const cards: Record<string, number> = {};
  const branches = new Set<string>();
  const items: string[] = [];
  for (const w of waiting) {
    if (w.segment !== null) segments[w.segment] = (segments[w.segment] ?? 0) + 1;
    cards[w.card] = (cards[w.card] ?? 0) + 1;
    for (const b of w.branches) branches.add(b);
    items.push(...w.items);
  }
  return { count: waiting.length, waiting, segments, cards, branches: [...branches], items };
}

/** The perk steps the Heritage in hand buys now, with their branches: where unseen Heritage leads. */
export function heritageMarks(
  state: IdleWorldState,
  content: IdleContent,
): { items: string[]; branches: string[] } {
  const have = state.permanent.heritage;
  const items: string[] = [];
  const branches: string[] = [];
  for (const b of idlePerkTree(state, content))
    for (const t of b.tiers)
      for (const s of t.steps) {
        if (s.atTop || s.state === 'locked' || s.state === 'closed') continue;
        const perk = content.perks.find((k) => k.id === s.id);
        if (!perk || idlePerkCost(perk, s.level) > have) continue;
        items.push(s.id);
        if (!branches.includes(b.branch)) branches.push(b.branch);
      }
  return { items, branches };
}

/**
 * Heritage earned since Perks was last opened. `seen` is null when this device has no record
 * for the world, which shows none unseen.
 */
export function heritageUnseen(state: IdleWorldState, seen: number | null): boolean {
  return seen !== null && state.permanent.heritageEarned > seen;
}

/**
 * D461: what the player had seen when they last looked, per world on this phone: the goal tiers
 * met (as `metGoalKeys` gives them), the rosettes earned and the week whose prize was seen.
 */
export interface NewSeen {
  goals: string[];
  rosettes: number;
  week: number;
}

/**
 * The goal tiers met now, each tagged with its generation and year (long goals with their
 * generation), so a goal met again in a later year is new again.
 */
export function metGoalKeys(state: IdleWorldState): string[] {
  const gen = state.permanent.lineage?.generation ?? 1;
  return [
    ...state.year.goals.map((k) => `${gen}:${state.year.number}:${k}`),
    ...idleLongGoalsMet(state).map((k) => `${gen}:long:${k}`),
  ];
}

/** A tagged key's goal key, for its name and pay: `1:3:own_25_wheat@2` -> `own_25_wheat@2`. */
export const goalKeyOf = (tagged: string): string => tagged.slice(tagged.lastIndexOf(':') + 1);

/** What there is to see now; stored when the player looks, and as the start for a new record. */
export function newSeenNow(state: IdleWorldState, content: IdleContent, t: number): NewSeen {
  const week = idleEventNow(state, content, t).won ? (state.daily?.week ?? -1) : -1;
  return { goals: metGoalKeys(state), rosettes: idleRosettes(state).length, week };
}

/** The goal tiers met since Goals was last looked at, as goal keys. None with no record. */
export function goalsNew(state: IdleWorldState, seen: NewSeen | null): string[] {
  if (!seen) return [];
  const had = new Set(seen.goals);
  return metGoalKeys(state)
    .filter((k) => !had.has(k))
    .map(goalKeyOf);
}

/** The rosettes earned since their sheet was last opened, newest last. None with no record. */
export function rosettesNew(state: IdleWorldState, seen: NewSeen | null): string[] {
  if (!seen) return [];
  const all = idleRosettes(state);
  return all.slice(Math.min(all.length, Math.max(0, seen.rosettes)));
}

/** The week's prize is won and its sheet not opened since. False with no record. */
export function prizeNew(
  state: IdleWorldState,
  content: IdleContent,
  t: number,
  seen: NewSeen | null,
): boolean {
  if (!seen || !idleEventNow(state, content, t).won) return false;
  return state.daily?.week !== seen.week;
}

/**
 * Every tab's badge for the world at `t`. `heritageSeen` is the Heritage earned when Perks was
 * last opened on this device, or null with no record; `news` what was seen of goals, rosettes and
 * the week's prize (D461), or null with no record.
 */
export function idleBadges(
  state: IdleWorldState,
  content: IdleContent,
  t: number,
  heritageSeen: number | null,
  news: NewSeen | null = null,
): IdleBadges {
  const almanac: Waiting[] = goalsNew(state, news).map((k) => ({
    segment: 'goals',
    card: 'goals-new',
    branches: [],
    items: [k],
  }));

  const festival: Waiting[] = [];
  if (idleFestivalAvailable(state, content))
    festival.push({
      segment: 'year',
      card: 'festival-card',
      branches: [],
      items: ['festival-hold'],
    });
  // Perks shows the tree only after the generation's first festival.
  if (heritageUnseen(state, heritageSeen) && idleFestivalsThisGeneration(state) > 0)
    festival.push({ segment: 'good', card: 'heritage-card', ...heritageMarks(state, content) });

  const region: Waiting[] = [];
  idleOrders(state, content, t).forEach((o, i) => {
    if (o.ready)
      region.push({ segment: null, card: 'village-orders', branches: [], items: [`order-${i}`] });
  });
  for (const id of rosettesNew(state, news))
    region.push({ segment: null, card: 'village-rosettes', branches: [], items: [id] });
  if (prizeNew(state, content, t, news))
    region.push({ segment: null, card: 'village-event', branches: [], items: ['prize'] });

  return {
    world: tabBadge([]),
    almanac: tabBadge(almanac),
    festival: tabBadge(festival),
    region: tabBadge(region),
  };
}

/** The segment a badged tab opens on: the one holding its first waiting thing. */
export function firstSegment(b: TabBadge): string | null {
  return b.waiting.find((w) => w.segment !== null)?.segment ?? null;
}

/** A marked card's line, saying what waits: `1 order ready to send`, `2 orders ready to send`. */
export function ordersWaitingLine(n: number): string {
  return `${n} ${n === 1 ? 'order' : 'orders'} ready to send`;
}

/** D461: how many new goals Goals shows before "and N more". */
export const NEW_GOALS_SHOWN = 5;

/**
 * D461: the newest `shown` of the goals new since last looked (met last first), and how many
 * more there are behind "and N more".
 */
export function newestGoals<T>(
  items: readonly T[],
  shown = NEW_GOALS_SHOWN,
): { shown: T[]; more: number } {
  const newest = [...items].reverse();
  return { shown: newest.slice(0, shown), more: Math.max(0, newest.length - shown) };
}

/** D461: a card's line for things new: `1 new: First rosette`, or `2 new`. */
export function newLine(names: string[]): string {
  return names.length === 1 ? `1 new: ${names[0]!}` : `${names.length} new`;
}
